<template>
  <svg :id="svgId" class="line-graph-container"></svg>
</template>

<script>
import * as d3 from "d3";

export default {
  name: "LineGraph",
  props: {
    dataInput: Array,
    width: Number,
    height: Number,
    mean: Boolean,
    median: Boolean,
    standDeviation: Boolean,
    variance: Boolean,
    suggest: Boolean,
  },
  data() {
    return {
      svgId: `linechart-${this._uid}`,
    };
  },
  mounted() {
    this.drawChart();
  },
  methods: {
    drawChart() {
      const data = [...this.dataInput];
      const suggestions = { lower: 30, middle: 50, upper: 80 };
      data.sort((a, b) => a.value - b.value);
      const maxValue = 100;

      d3.select(`#${this.svgId}`).selectAll('*').remove();

      const margin = { top: 40, right: 180, bottom: 40, left: 60 };
      const width = this.width;
      const height = this.height;

      const svg = d3.select(`#${this.svgId}`)
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .style("font-family", "Times New Roman")
        .append("g")
        .attr("transform", `translate(${margin.left}, ${margin.top})`);

      // Add bounding box with transparent fill and light stroke
      svg
        .append('rect')
        .attr('x', -margin.left)
        .attr('y', -margin.top)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .attr('rx', 12)
        .attr('fill', 'none')
        .attr('stroke', '#ccc')
        .attr('stroke-width', 1);

      const x = d3.scaleBand()
        .range([0, width])
        .padding(0.3)
        .domain(data.map((d) => d.variable));

      const y = d3.scaleLinear()
        .range([height, 0])
        .domain([0, maxValue]);

      svg.append("g")
        .attr("class", "grid")
        .call(d3.axisLeft(y).tickSize(-width).tickFormat(""))
        .selectAll("line")
        .style("stroke", "#e0e0e0")
        .style("stroke-dasharray", "2,2");

      svg.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0, ${height})`)
        .call(d3.axisBottom(x).tickSize(0))
        .selectAll("text")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end")
        .style("fill", "#333");

      svg.append("g")
        .attr("class", "y-axis")
        .call(d3.axisLeft(y).tickSize(5))
        .selectAll("text")
        .style("fill", "#333");

      svg.append('text')
        .attr('text-anchor', 'middle')
        .attr('transform', `translate(${-margin.left + 25}, ${height / 2}) rotate(-90)`)
        .attr('font-size', '13px')
        .attr('fill', '#333')
        .text('Score (%)');


      svg.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', width / 2)
        .attr('y', height + margin.bottom - 15)
        .attr('font-size', '13px')
        .attr('fill', '#333')
        .text('Categories');

      const line = d3.line()
        .x((d) => x(d.variable) + x.bandwidth() / 2)
        .y((d) => y(d.value));

      svg.append("path")
        .datum(data)
        .attr("class", "line")
        .attr("d", line)
        .attr("fill", "none")
        .attr("stroke", "#007ACC")
        .attr("stroke-width", 2);

      if (this.suggest) {
        const suggestionsAxis = d3.axisRight()
          .scale(y)
          .tickValues([suggestions.lower, suggestions.middle, suggestions.upper])
          .tickSizeInner(-width)
          .tickFormat((d, i) => ['Needs Improvement', 'Good', 'Excellent'][i]);

        svg.append("g")
          .attr("class", "suggestionsAxis")
          .attr("transform", `translate(${width}, 0)`)
          .call(suggestionsAxis)
          .select(".domain")
          .remove();

        svg.selectAll(".suggestionsAxis .tick line")
          .attr("stroke-dasharray", "2,2")
          .style("stroke", (d, i) => ['#ef5350', '#ffa726', '#66bb6a'][i]);
      }

      const meanValue = d3.mean(data, d => d.value);
      const medianValue = d3.median(data, d => d.value);
      const stdDeviation = d3.deviation(data, d => d.value);
      const varianceValue = d3.variance(data, d => d.value);
      const keyData = [];

      if (this.mean && meanValue != null) {
        keyData.push({ label: 'Mean', color: '#e53935', value: meanValue.toFixed(2) });
        svg.append("line")
          .attr("x1", 0).attr("x2", width)
          .attr("y1", y(meanValue)).attr("y2", y(meanValue))
          .attr("stroke", "#e53935").attr("stroke-width", 2);
      }

      if (this.median && medianValue != null) {
        keyData.push({ label: 'Median', color: '#1e88e5', value: medianValue.toFixed(2) });
        svg.append("line")
          .attr("x1", 0).attr("x2", width)
          .attr("y1", y(medianValue)).attr("y2", y(medianValue))
          .attr("stroke", "#1e88e5").attr("stroke-width", 2);
      }

      if (this.standDeviation && stdDeviation != null && meanValue != null) {
        keyData.push({ label: 'SD', color: '#43a047', value: stdDeviation.toFixed(2) });
        svg.append("line")
          .attr("x1", 0).attr("x2", width)
          .attr("y1", y(meanValue + stdDeviation)).attr("y2", y(meanValue + stdDeviation))
          .attr("stroke", "#43a047").attr("stroke-width", 2);
        svg.append("line")
          .attr("x1", 0).attr("x2", width)
          .attr("y1", y(meanValue - stdDeviation)).attr("y2", y(meanValue - stdDeviation))
          .attr("stroke", "#43a047").attr("stroke-width", 2);
      }

      if (this.variance && varianceValue != null) {
        keyData.push({ label: 'Variance', color: '#fb8c00', value: varianceValue.toFixed(2) });
        svg.append("line")
          .attr("x1", 0).attr("x2", width)
          .attr("y1", y(varianceValue)).attr("y2", y(varianceValue))
          .attr("stroke", "#fb8c00").attr("stroke-width", 2);
      }

      const keySvg = d3.select(`#${this.svgId}`);
      const keyItems = keySvg.selectAll('.key-item')
        .data(keyData)
        .enter()
        .append('g')
        .attr('class', 'key-item')
        .attr('transform', (d, i) => `translate(${width + margin.left + 40}, ${i * 24 + 35})`); // 24px vertical spacing

      keyItems.append('line')
        .attr('x1', 0).attr('y1', 5)
        .attr('x2', 20).attr('y2', 5)
        .attr('stroke', d => d.color)
        .attr('stroke-width', 2);

      keyItems.append('text')
        .attr('x', 30).attr('y', 10)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .text(d => `${d.label}:`)
        .style('fill', '#333');

      keyItems.append('text')
        .attr('x', 100).attr('y', 10)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .text(d => `${d.value}`)
        .style('fill', '#333');
    }
  }
};
</script>

<style>
.line {
  fill: none;
  stroke: steelblue;
  stroke-width: 2px;
}
</style>
